import React, { useMemo, useState } from "react";
import { ActionIcon, Box, Text, UnstyledButton } from "@mantine/core";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { addDays, dayKeyOf, formatDate, startOfWeek } from "../grid/geometry";
import { MobileSheet } from "./MobileSheet";
import styles from "./MobileDatePickerSheet.module.css";

export interface MobileDatePickerSheetProps {
  opened: boolean;
  onClose: () => void;
  selectedDate: Date;
  todayKey: string;
  /** Days ("YYYY-MM-DD") that have appointments (dot). */
  busyDays: Set<string>;
  /** Salon-closed days: hatched for reference, still pickable. */
  isClosed: (date: Date) => boolean;
  /** The picked day. */
  onSelectDate: (date: Date) => void;
}

const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

const PickerBody: React.FC<Omit<MobileDatePickerSheetProps, "opened">> = ({
  onClose,
  selectedDate,
  todayKey,
  busyDays,
  isClosed,
  onSelectDate,
}) => {
  const [month, setMonth] = useState(
    () => new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1),
  );
  const selectedKey = dayKeyOf(selectedDate);

  const rows = useMemo(() => {
    const gridStart = startOfWeek(month);
    const selectedWeek = startOfWeek(selectedDate);
    return Array.from({ length: 6 }, (_, r) => {
      const weekStart = addDays(gridStart, r * 7);
      const diff = Math.round(
        (weekStart.getTime() - selectedWeek.getTime()) / WEEK_MS,
      );
      return {
        rel: diff === 0 ? "now" : `${diff > 0 ? "+" : ""}${diff}w`,
        now: diff === 0,
        days: Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
      };
    });
  }, [month, selectedDate]);

  return (
    <>
      <Box className={styles.monthRow}>
        <Text span className={styles.monthLabel}>
          {formatDate(month, { month: "long", year: "numeric" })}
        </Text>
        <Box className={styles.monthNav}>
          <ActionIcon
            variant="subtle"
            className={styles.navBtn}
            onClick={() =>
              setMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))
            }
            aria-label="Previous month"
          >
            <IconChevronLeft size={17} stroke={1.8} />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            className={styles.navBtn}
            onClick={() =>
              setMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))
            }
            aria-label="Next month"
          >
            <IconChevronRight size={17} stroke={1.8} />
          </ActionIcon>
        </Box>
      </Box>

      <Box className={`${styles.grid} ${styles.dowRow}`}>
        <Box />
        {DOW.map((d) => (
          <Text span key={d} className={styles.dowCell}>
            {d}
          </Text>
        ))}
      </Box>

      <Box className={styles.weeks}>
        {rows.map((row) => (
          <Box key={dayKeyOf(row.days[0])} className={styles.grid}>
            <Text span className={styles.rel} data-now={row.now}>
              {row.rel}
            </Text>
            {row.days.map((day) => {
              const key = dayKeyOf(day);
              const inMonth = day.getMonth() === month.getMonth();
              const closed = isClosed(day);
              const selected = key === selectedKey;
              return (
                <UnstyledButton
                  key={key}
                  className={styles.cell}
                  data-outside={!inMonth}
                  data-closed={closed && inMonth}
                  data-today={key === todayKey}
                  data-selected={selected}
                  aria-pressed={selected}
                  aria-label={formatDate(day, {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                  onClick={() => {
                    onSelectDate(day);
                    onClose();
                  }}
                >
                  {day.getDate()}
                  {busyDays.has(key) && inMonth && !selected && (
                    <Box component="span" className={styles.dot} />
                  )}
                </UnstyledButton>
              );
            })}
          </Box>
        ))}
      </Box>
    </>
  );
};

/**
 * Date picker sheet: one exact day on a month grid with a relative-week
 * gutter, appointment dots, and a teal selected day. Picking a day reports it
 * and closes.
 */
export const MobileDatePickerSheet: React.FC<MobileDatePickerSheetProps> = ({
  opened,
  ...rest
}) => (
  <MobileSheet
    opened={opened}
    onClose={rest.onClose}
    title="Pick a date"
    data-testid="mobile-date-sheet"
  >
    <PickerBody {...rest} />
  </MobileSheet>
);
